import { Slot } from "radix-ui";
import { forwardRef } from "react";
import type { CSSProperties, ReactNode } from "react";

import {
  alignItems,
  colorValue,
  fontSize,
  fontWeight,
  justifyContent,
  lineHeight,
  space,
  splitProps,
} from "./utils";

// Resolve a Radix responsive prop to its base value.
function val<T>(v: T | Record<string, T> | undefined): T | undefined {
  if (v && typeof v === "object") {
    const o = v as Record<string, T>;
    return o.initial ?? Object.values(o)[0];
  }
  return v as T;
}

type StyledProps = Record<string, any> & {
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  asChild?: boolean;
  as?: any;
};

function columnsToTemplate(v: unknown): string | undefined {
  const t = val(v);
  if (t == null) return undefined;
  const s = String(t);
  if (/^\d+$/.test(s)) return `repeat(${s}, minmax(0, 1fr))`;
  return s;
}

export const Box = forwardRef<any, StyledProps>(function Box(props, ref) {
  const { className, children, asChild, as } = props;
  const { style, rest } = splitProps(props);
  const Comp: any = asChild ? Slot.Root : (as ?? "div");
  return (
    <Comp ref={ref} className={className} style={style} {...rest}>
      {children}
    </Comp>
  );
});

export const Flex = forwardRef<any, StyledProps>(function Flex(props, ref) {
  const { className, children, asChild, as } = props;
  const flexStyle: CSSProperties = {
    display: (props.display as any) ?? "flex",
    flexDirection: val(props.direction) as any,
    alignItems: alignItems(props.align),
    justifyContent: justifyContent(props.justify),
    flexWrap: val(props.wrap) as any,
  };
  const fGap = space(props.gap);
  const fGapX = space(props.gapX);
  const fGapY = space(props.gapY);
  if (fGap != null) flexStyle.gap = fGap;
  if (fGapX != null) flexStyle.columnGap = fGapX;
  if (fGapY != null) flexStyle.rowGap = fGapY;
  const { style, rest } = splitProps(props, flexStyle);
  const Comp: any = asChild ? Slot.Root : (as ?? "div");
  return (
    <Comp ref={ref} className={className} style={style} {...rest}>
      {children}
    </Comp>
  );
});

export const Grid = forwardRef<any, StyledProps>(function Grid(props, ref) {
  const { className, children, asChild, as } = props;
  const gridStyle: CSSProperties = {
    display: (props.display as any) ?? "grid",
    gridTemplateColumns: columnsToTemplate(props.columns),
    gridTemplateRows: columnsToTemplate(props.rows),
    gridAutoFlow: val(props.flow) as any,
    gridTemplateAreas: props.areas as any,
    alignItems: alignItems(props.align),
    justifyContent: justifyContent(props.justify),
  };
  const gGap = space(props.gap);
  const gGapX = space(props.gapX);
  const gGapY = space(props.gapY);
  if (gGap != null) gridStyle.gap = gGap;
  if (gGapX != null) gridStyle.columnGap = gGapX;
  if (gGapY != null) gridStyle.rowGap = gGapY;
  const { style, rest } = splitProps(props, gridStyle);
  const Comp: any = asChild ? Slot.Root : (as ?? "div");
  return (
    <Comp ref={ref} className={className} style={style} {...rest}>
      {children}
    </Comp>
  );
});

export const Section = forwardRef<any, StyledProps>(
  function Section(props, ref) {
    const { className, children } = props;
    const { style, rest } = splitProps(props, { width: "100%" });
    return (
      <section ref={ref} className={className} style={style} {...rest}>
        {children}
      </section>
    );
  },
);

export const Container = forwardRef<any, StyledProps>(
  function Container(props, ref) {
    const { className, children } = props;
    const { style, rest } = splitProps(props, {
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
    });
    return (
      <div ref={ref} className={className} style={style} {...rest}>
        {children}
      </div>
    );
  },
);

/** Radix Inset bleeds content to the edge of a padded container. */
export const Inset = forwardRef<any, StyledProps>(function Inset(props, ref) {
  const { className, children } = props;
  const { style, rest } = splitProps(props);
  return (
    <div ref={ref} className={className} style={style} {...rest}>
      {children}
    </div>
  );
});

/** Lightweight overflow-based replacement for Radix ScrollArea. */
export const ScrollArea = forwardRef<any, StyledProps>(
  function ScrollArea(props, ref) {
    const { className, children } = props;
    const scrollbars = (val(props.scrollbars) as string) ?? "vertical";
    const overflow: CSSProperties =
      scrollbars === "horizontal"
        ? { overflowX: "auto", overflowY: "hidden" }
        : scrollbars === "both"
          ? { overflow: "auto" }
          : { overflowX: "hidden", overflowY: "auto" };
    const { style, rest } = splitProps(props, { ...overflow, minHeight: 0 });
    // strip Radix-only props not handled by splitProps
    delete (rest as any).type;
    delete (rest as any).scrollbars;
    return (
      <div ref={ref} className={className} style={style} {...rest}>
        {children}
      </div>
    );
  },
);

export const Separator = forwardRef<any, StyledProps>(
  function Separator(props, ref) {
    const { className, orientation } = props;
    const o = (val(orientation) as "horizontal" | "vertical") ?? "horizontal";
    const { style, rest } = splitProps(props, {
      flexShrink: 0,
      backgroundColor: "hsl(var(--border))",
      ...(o === "vertical"
        ? { width: "1px", height: "100%", alignSelf: "stretch" }
        : { height: "1px", width: "100%" }),
    });
    delete (rest as any).orientation;
    delete (rest as any).decorative;
    return (
      <div
        ref={ref}
        role="separator"
        aria-orientation={o}
        className={className}
        style={style}
        {...rest}
      />
    );
  },
);

export const Text = forwardRef<any, StyledProps>(function Text(props, ref) {
  const { className, children, asChild, as } = props;
  const textStyle: CSSProperties = {
    fontSize: fontSize(props.size),
    lineHeight: lineHeight(props.size),
    fontWeight: fontWeight(props.weight) as any,
    color: colorValue(props.color),
    textAlign: val(props.align) as any,
  };
  if (props.truncate) {
    textStyle.overflow = "hidden";
    textStyle.textOverflow = "ellipsis";
    textStyle.whiteSpace = "nowrap";
  }
  const { style, rest } = splitProps(props, textStyle);
  delete (rest as any).truncate;
  const Comp: any = asChild ? Slot.Root : (as ?? "span");
  return (
    <Comp ref={ref} className={className} style={style} {...rest}>
      {children}
    </Comp>
  );
});

export const Heading = forwardRef<any, StyledProps>(
  function Heading(props, ref) {
    const { className, children, as } = props;
    const headingStyle: CSSProperties = {
      fontSize: fontSize(props.size ?? "6"),
      lineHeight: lineHeight(props.size ?? "6"),
      fontWeight: (fontWeight(props.weight ?? "bold") as any) ?? 700,
      color: colorValue(props.color),
      textAlign: val(props.align) as any,
      letterSpacing: "-0.01em",
    };
    const { style, rest } = splitProps(props, headingStyle);
    const Comp: any = as ?? "h2";
    return (
      <Comp ref={ref} className={className} style={style} {...rest}>
        {children}
      </Comp>
    );
  },
);

export const Link = forwardRef<any, StyledProps>(function Link(props, ref) {
  const { className, children, asChild } = props;
  const linkStyle: CSSProperties = {
    color: colorValue(props.color) ?? "hsl(var(--info))",
    textDecoration: props.underline === "always" ? "underline" : undefined,
    cursor: "pointer",
    fontSize: fontSize(props.size),
    fontWeight: fontWeight(props.weight) as any,
  };
  const { style, rest } = splitProps(props, linkStyle);
  delete (rest as any).underline;
  const Comp: any = asChild ? Slot.Root : "a";
  return (
    <Comp ref={ref} className={className} style={style} {...rest}>
      {children}
    </Comp>
  );
});

export type TextProps = StyledProps;
